/**
 * Binary audio frame encoding/decoding — docs/PROTOCOL.md §3.1.
 *
 * Layout (little-endian, 16-byte header):
 *
 *   0  u8   magic 0xAC   |  8  u32 timestamp (48 kHz sample clock)
 *   1  u8   version      | 12  u16 sampleCount (per channel)
 *   2  u8   payloadType  | 14  u8  peakL
 *   3  u8   flags        | 15  u8  peakR
 *   4  u32  seq          | 16  …   payload
 */
import { FRAME_HEADER_BYTES, LIMITS, MAGIC, PROTOCOL_VERSION } from './limits.js';
export const PayloadType = {
    OPUS: 1,
    PCM_F32LE: 2,
};
export const FrameFlags = {
    NONE: 0,
    /** Encoder produced this from digital silence. Not the same as a lost frame. */
    SILENCE: 1 << 0,
    /** First frame after a stall or encoder reset — reset the jitter buffer. */
    DISCONTINUITY: 1 << 1,
};
export class FrameDecodeError extends Error {
    reason;
    constructor(message, reason) {
        super(message);
        this.reason = reason;
        this.name = 'FrameDecodeError';
    }
}
/** Clamp a linear 0..1 peak into the header's u8 slot. */
export function encodePeak(linear) {
    if (!Number.isFinite(linear) || linear <= 0)
        return 0;
    return Math.min(255, Math.round(linear * 255));
}
export function decodePeak(byte) {
    return byte / 255;
}
/**
 * Build a complete binary frame. Allocates once; the payload is copied in.
 *
 * The server never calls this — it forwards bytes verbatim. It exists for the
 * headless test sender, for tests, and as the executable spec the C++ encoder is
 * checked against.
 */
export function encodeFrame(payload, opts) {
    const out = new Uint8Array(FRAME_HEADER_BYTES + payload.byteLength);
    const view = new DataView(out.buffer);
    out[0] = MAGIC;
    out[1] = PROTOCOL_VERSION;
    out[2] = opts.payloadType ?? PayloadType.OPUS;
    out[3] = opts.flags ?? FrameFlags.NONE;
    // `>>> 0` so a caller passing a negative or oversized counter still lands in range
    // rather than throwing — sequence numbers are defined to wrap.
    view.setUint32(4, opts.seq >>> 0, true);
    view.setUint32(8, opts.timestamp >>> 0, true);
    view.setUint16(12, opts.sampleCount & 0xffff, true);
    out[14] = encodePeak(opts.peakL ?? 0);
    out[15] = encodePeak(opts.peakR ?? opts.peakL ?? 0);
    out.set(payload, FRAME_HEADER_BYTES);
    return out;
}
/**
 * Parse a frame. Returns a payload *view* into `data` — zero-copy, so callers that
 * need to keep the bytes must copy them.
 *
 * Throws {@link FrameDecodeError} rather than returning null so that callers cannot
 * accidentally ignore a malformed frame.
 */
export function decodeFrame(data) {
    if (data.byteLength < FRAME_HEADER_BYTES) {
        throw new FrameDecodeError(`frame is ${data.byteLength} bytes, need at least ${FRAME_HEADER_BYTES}`, 'too-short');
    }
    if (data.byteLength > LIMITS.MAX_FRAME_BYTES) {
        throw new FrameDecodeError(`frame is ${data.byteLength} bytes, limit is ${LIMITS.MAX_FRAME_BYTES}`, 'too-large');
    }
    if (data[0] !== MAGIC) {
        throw new FrameDecodeError(`bad magic 0x${(data[0] ?? 0).toString(16)}, expected 0x${MAGIC.toString(16)}`, 'bad-magic');
    }
    const version = data[1];
    if (version !== PROTOCOL_VERSION) {
        throw new FrameDecodeError(`unsupported frame version ${version}`, 'bad-version');
    }
    const payloadType = data[2];
    if (payloadType !== PayloadType.OPUS && payloadType !== PayloadType.PCM_F32LE) {
        throw new FrameDecodeError(`unknown payload type ${payloadType}`, 'bad-payload-type');
    }
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const sampleCount = view.getUint16(12, true);
    const payload = data.subarray(FRAME_HEADER_BYTES);
    // PCM is self-describing, so we can catch truncation that the codec would
    // otherwise silently paper over. An Opus packet's length is only meaningful to
    // the decoder, so there is nothing to check for it here.
    if (payloadType === PayloadType.PCM_F32LE) {
        const bytesPerChannel = sampleCount * Float32Array.BYTES_PER_ELEMENT;
        const channels = bytesPerChannel === 0 ? 0 : payload.byteLength / bytesPerChannel;
        if (!LIMITS.SUPPORTED_CHANNELS.includes(channels)) {
            throw new FrameDecodeError(`PCM payload of ${payload.byteLength} B does not hold 1 or 2 channels of ` +
                `${sampleCount} f32 samples`, 'length-mismatch');
        }
    }
    return {
        version,
        payloadType: payloadType,
        flags: data[3],
        seq: view.getUint32(4, true),
        timestamp: view.getUint32(8, true),
        sampleCount,
        peakL: decodePeak(data[14]),
        peakR: decodePeak(data[15]),
        payload,
    };
}
/**
 * Cheap validity gate for the relay's ingest path. The relay must not fully parse
 * frames (it is an opaque pipe), but it does refuse obvious garbage so a broken
 * sender cannot poison every listener's decoder.
 */
export function isPlausibleFrame(data) {
    return (data.byteLength >= FRAME_HEADER_BYTES &&
        data.byteLength <= LIMITS.MAX_FRAME_BYTES &&
        data[0] === MAGIC &&
        data[1] === PROTOCOL_VERSION);
}
export function hasFlag(flags, flag) {
    return (flags & flag) !== 0;
}
/**
 * Frames missing between two sequence numbers, accounting for the 2^32 wrap.
 * Returns 0 for a duplicate or out-of-order frame (TCP means this should not
 * happen, but the decoder must not produce a nonsense PLC burst if it does).
 */
export function gapBetween(prevSeq, seq) {
    const delta = (seq - prevSeq) >>> 0;
    if (delta === 0 || delta > 0x7fffffff)
        return 0;
    return delta - 1;
}
//# sourceMappingURL=frame.js.map